import { useMemo, useState } from 'react'
import { POLL } from '@/lib/pollTiers'
import { useQuery } from '@tanstack/react-query'
import { TrendingUp, TrendingDown, Minus, ChevronDown } from 'lucide-react'
import clsx from 'clsx'
import { supabase } from '@/lib/supabase'
import { useSaveDb } from '@/lib/useSaveDb'
import { withReadTimeout, DASHBOARD_WIDGET_TIMEOUT_MS } from '@/lib/saveTimeout'
import WidgetRetry from '@/components/WidgetRetry'
import { BrandLoader } from '@/components/BrandLoader'
import { useDeviceClass } from '@/hooks/useDeviceClass'
import { useTenantPersistedState } from '@/hooks/useTenantPersistedState'

// ---------------------------------------------------------------------------
// Hook — fetch pallets packed + bins tipped, bucketed per day OR per hour
// depending on granularity. SAST (UTC+2) is the local-time axis.
// ---------------------------------------------------------------------------
type Granularity = 'day' | 'hour'
type Point = { ts: number; pallets: number; bins: number }

const SAST_OFFSET_MS = 2 * 60 * 60 * 1000

// Bucket a UTC ISO timestamp to either the start-of-SAST-day or
// start-of-SAST-hour, returning a UTC ms epoch.
function bucketStart(iso: string, gran: Granularity): number {
  const sastMs = new Date(iso).getTime() + SAST_OFFSET_MS
  const truncated = gran === 'day'
    ? Math.floor(sastMs / (24 * 60 * 60 * 1000)) * (24 * 60 * 60 * 1000)
    : Math.floor(sastMs / (60 * 60 * 1000))     * (60 * 60 * 1000)
  return truncated - SAST_OFFSET_MS  // back to UTC ms
}

function useTrend(days: number, gran: Granularity) {
  // Read through the isolated lock-free client so this dashboard widget can't be
  // wedged behind the main client's in-process auth-token lock (see useSaveDb /
  // supabase.ts AUTH_TIMEOUT_MS — a slow refresh queues every rpc() on the shared
  // client past the 15s widget timeout). Falls back to the shared client during
  // the cold-boot window before a claim-bearing token is captured. packout_trend
  // is security_invoker → RLS scopes by active_tenant_id, which the token carries.
  const trendDb = useSaveDb()
  return useQuery({
    queryKey: ['packout-trend', days, gran],
    queryFn: async (): Promise<Point[]> => {
      // A read, not a save (UX-035 family): no slow_save/save_timeout telemetry.
      const { data, error } = await withReadTimeout(
        (trendDb ?? supabase).rpc('packout_trend', { p_days: days, p_gran: gran }),
        'PackoutTrendChart: packout_trend',
        DASHBOARD_WIDGET_TIMEOUT_MS,
      )
      if (error) throw error

      // Server-bucketed (mig 944): bucket_start is the UTC instant of each SAST
      // day/hour start — the same key bucketStart() produces — so the gap-fill
      // loop below lines up with no change. Replaces two .range(0,49_999) pulls
      // (pallets + tipping_log) that ran on every Dashboard load, every 5 min.
      const buckets = new Map<number, { pallets: number; bins: number }>()
      for (const row of (data ?? []) as { bucket_start: string; pallets: number; bins: number }[]) {
        buckets.set(new Date(row.bucket_start).getTime(), {
          pallets: Number(row.pallets ?? 0),
          bins: Number(row.bins ?? 0),
        })
      }

      // Fill every bucket in the window so the line is continuous.
      const stepMs = gran === 'day' ? 24 * 60 * 60 * 1000 : 60 * 60 * 1000
      const nowBucket = bucketStart(new Date().toISOString(), gran)
      const totalBuckets = gran === 'day' ? days : days * 24
      const out: Point[] = []
      for (let i = totalBuckets - 1; i >= 0; i--) {
        const ts = nowBucket - i * stepMs
        const b = buckets.get(ts) ?? { pallets: 0, bins: 0 }
        out.push({ ts, ...b })
      }
      return out
    },
    refetchInterval: POLL.passive,
  })
}

// ---------------------------------------------------------------------------
// Pure SVG line chart — no dependencies. Two overlaid series with Y axes
// implied by colour rather than drawn (deliberately minimal).
// ---------------------------------------------------------------------------
type Series = { label: string; values: number[]; colour: string }

type XTick = { index: number; label: string }

function LineChart({ series, labels, xTicks, dayDividers = [], height = 140 }: {
  series: Series[]
  labels: string[]               // hover-tooltip labels, indexed per bucket
  xTicks?: XTick[]               // optional explicit axis ticks; defaults to first/middle/last
  dayDividers?: number[]         // bucket indices where a new SAST day starts (faint vertical line)
  height?: number
}) {
  const [hover, setHover] = useState<number | null>(null)

  const w = 600                 // virtual viewBox width — scales responsively
  const h = height
  const padL = 8, padR = 8, padT = 14, padB = 22
  const stepX = (w - padL - padR) / Math.max(1, labels.length - 1)
  const xAt = (i: number) => padL + stepX * i

  // Each series gets its own Y scale so they can co-exist visually.
  const seriesPaths = series.map(s => {
    const max = Math.max(1, ...s.values)
    const points = s.values.map((v, i) => {
      const y = h - padB - (v / max) * (h - padT - padB)
      return [xAt(i), y] as const
    })
    const d = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
    const area = points.length > 0
      ? `${d} L${points[points.length - 1][0].toFixed(1)},${(h - padB).toFixed(1)} L${points[0][0].toFixed(1)},${(h - padB).toFixed(1)} Z`
      : ''
    return { ...s, points, d, area, max }
  })

  const ticks: XTick[] = xTicks ?? (
    labels.length <= 3
      ? labels.map((label, index) => ({ index, label }))
      : [
          { index: 0,                              label: labels[0] },
          { index: Math.floor(labels.length / 2),  label: labels[Math.floor(labels.length / 2)] },
          { index: labels.length - 1,              label: labels[labels.length - 1] },
        ]
  )

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none"
        onPointerMove={e => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect()
          const xRel = ((e.clientX - rect.left) / rect.width) * w
          const usable = w - padL - padR
          const i = Math.round(((xRel - padL) / usable) * (labels.length - 1))
          setHover(Math.max(0, Math.min(labels.length - 1, i)))
        }}
        onPointerLeave={() => setHover(null)}>
        {/* Gridlines — 3 horizontal */}
        {[0.25, 0.5, 0.75].map(f => (
          <line key={f} x1={padL} x2={w - padR} y1={padT + (h - padT - padB) * f}
            y2={padT + (h - padT - padB) * f} stroke="rgb(55 65 81)" strokeDasharray="2 4" strokeWidth="0.5" />
        ))}

        {/* Day dividers — faint vertical lines at SAST midnight (hourly view) */}
        {dayDividers.map(i => (
          <line key={`div-${i}`} x1={xAt(i)} x2={xAt(i)} y1={padT} y2={h - padB}
            stroke="rgb(75 85 99)" strokeWidth="0.5" />
        ))}

        {/* Series — area + line */}
        {seriesPaths.map((s, idx) => (
          <g key={idx}>
            {s.area && <path d={s.area} fill={s.colour} opacity="0.12" />}
            <path d={s.d} fill="none" stroke={s.colour} strokeWidth="1.5"
              strokeLinejoin="round" strokeLinecap="round" />
          </g>
        ))}

        {/* Hover indicator */}
        {hover != null && seriesPaths.length > 0 && seriesPaths[0].points[hover] && (
          <>
            <line x1={seriesPaths[0].points[hover][0]} x2={seriesPaths[0].points[hover][0]}
              y1={padT} y2={h - padB} stroke="rgb(156 163 175)" strokeWidth="0.5" strokeDasharray="2 2" />
            {seriesPaths.map((s, i) => s.points[hover] && (
              <circle key={i} cx={s.points[hover][0]} cy={s.points[hover][1]} r="2.5"
                fill={s.colour} stroke="rgb(17 24 39)" strokeWidth="1" />
            ))}
          </>
        )}

        {/* X-axis ticks */}
        {ticks.map((t, idx) => {
          const x = xAt(t.index)
          const anchor = idx === 0 ? 'start'
                       : idx === ticks.length - 1 ? 'end'
                       : 'middle'
          return (
            <text key={idx} x={x} y={h - 6} fill="rgb(107 114 128)" fontSize="9" textAnchor={anchor}>
              {t.label}
            </text>
          )
        })}
      </svg>

      {/* Hover tooltip */}
      {hover != null && (
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 surface-sunk border hairline-strong rounded-lg px-2 py-1 text-[10px] pointer-events-none whitespace-nowrap shadow-lg">
          <p className="text-fg-muted">{labels[hover]}</p>
          {series.map((s, i) => (
            <p key={i} style={{ color: s.colour }}>
              {s.label}: <span className="tabular-nums font-semibold">{Math.round(s.values[hover]).toLocaleString('en-ZA')}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

// The loading / failed / empty / line-chart states under the stats line.
function TrendChartArea({ range, points, labels, xTicks, dayDividers, empty, isLoading, isError, isFetching, refetch }: {
  range: number
  points: Point[]
  labels: string[]
  xTicks: XTick[] | undefined
  dayDividers: number[]
  empty: boolean
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => unknown
}) {
  return (
    <>
      {/* Chart */}
      {isLoading ? (
        <div className="h-[140px] flex items-center justify-center">
          <BrandLoader className="w-6 h-6" />
        </div>
      ) : isError && points.length === 0 ? (
        // Failed with nothing cached to fall back on. Show a retry — NOT the
        // misleading "No activity" empty state this used to render on a timeout.
        <div className="h-[140px] flex items-center justify-center">
          <WidgetRetry busy={isFetching} onRetry={() => { void refetch() }} />
        </div>
      ) : empty ? (
        <div className="h-[140px] flex items-center justify-center text-xs text-fg-muted">
          No activity in the last {range} days
        </div>
      ) : (
        <>
          <LineChart
            labels={labels}
            xTicks={xTicks}
            dayDividers={dayDividers}
            series={[
              { label: 'Pallets', values: points.map(p => p.pallets), colour: 'rgb(52 211 153)' },
              { label: 'Bins',    values: points.map(p => p.bins),    colour: 'rgb(251 191 36)' },
            ]}
          />
          {/* Refresh failed but we have the last good chart — let them retry
              in place rather than reload the page. */}
          {isError && (
            <div className="mt-1 flex justify-end">
              <WidgetRetry stale busy={isFetching} onRetry={() => { void refetch() }} />
            </div>
          )}
        </>
      )}
    </>
  )
}

const TREND_LOOK: Record<string, { Icon: typeof Minus; colour: string }> = {
  up:   { Icon: TrendingUp,   colour: 'text-green-400' },
  down: { Icon: TrendingDown, colour: 'text-red-400' },
  flat: { Icon: Minus,        colour: 'text-fg-muted' },
}

// The bins-per-pallet change pill beside the stats.
function TrendDelta({ trend }: { trend: NonNullable<TrendInfo> }) {
  const { Icon: TrendIcon, colour: trendColour } = TREND_LOOK[trend.dir] ?? TREND_LOOK.flat
  return (
    <p className={clsx('ml-auto flex items-center gap-1 text-xs font-semibold tabular-nums', trendColour)}
      title={`Change in bins-per-pallet ratio over the last ${trend.blockLabel.replace('prior ', '')} vs the ${trend.blockLabel}. Lower ratio = better packout.`}>
      <TrendIcon size={12} />
      {trend.pct > 0 && '+'}{trend.pct}%
      {/* blockLabel, not a hardcoded "prior 3d": on the 7d range the
          granularity is HOURLY, so the figure is last-24h vs prior-24h.
          The label was computed at :231 and then never rendered, so the
          7d and 30d views showed two differently-scoped numbers under
          the same caption. */}
      <span className="text-fg-muted font-normal">ratio vs {trend.blockLabel}</span>
    </p>
  )
}

// The stats line + chart under the header; rendered only while the card is open.
type TrendTotals = { pallets: number; bins: number; binsPerPallet: number | null }
type TrendInfo = { dir: string; pct: number; blockLabel: string } | null

function TrendBody({ range, totals, trend, points, labels, xTicks, dayDividers, isLoading, isError, isFetching, refetch }: {
  range: number
  totals: TrendTotals
  trend: TrendInfo
  points: Point[]
  labels: string[]
  xTicks: XTick[] | undefined
  dayDividers: number[]
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => unknown
}) {
  return (
    <>
      {/* Stats line */}
      <div className="flex items-baseline gap-3 mb-2 text-sm flex-wrap">
        <p className="text-fg tabular-nums font-semibold">
          {totals.pallets.toLocaleString('en-ZA')}<span className="text-xs text-fg-muted ml-1">pallets</span>
        </p>
        <p className="text-fg-muted tabular-nums text-xs">
          {totals.bins.toLocaleString('en-ZA')} bins
        </p>
        {totals.binsPerPallet != null && (
          <p className="text-emerald-300 tabular-nums text-xs font-semibold">
            {totals.binsPerPallet.toFixed(2)}<span className="text-fg-muted font-normal ml-1">bins / pallet</span>
          </p>
        )}
        {trend && <TrendDelta trend={trend} />}
      </div>

      <TrendChartArea
        range={range} points={points} labels={labels} xTicks={xTicks} dayDividers={dayDividers}
        empty={totals.pallets === 0 && totals.bins === 0}
        isLoading={isLoading} isError={isError} isFetching={isFetching} refetch={refetch}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Header: title, legend, phone fold button, range chips
// ---------------------------------------------------------------------------
function TrendHeader({ range, onRange, granularity, isPhone, open, onToggle }: {
  range: 7 | 14 | 30
  onRange: (r: 7 | 14 | 30) => void
  granularity: Granularity
  isPhone: boolean
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className={clsx('flex items-center justify-between gap-2', open && 'mb-2')}>
      {isPhone && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={open ? 'Hide the pallets and bins chart' : 'Show the pallets and bins chart'}
          className="tap-target shrink-0 flex items-center justify-center text-fg-muted">
          <ChevronDown size={16} className={clsx('transition-transform', !open && '-rotate-90')} />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-fg-muted uppercase tracking-wider">
          Pallets and bins · last {range} days
          {granularity === 'hour' && <span className="ml-1.5 text-[10px] text-fg-muted normal-case font-normal">· hourly</span>}
        </p>
        <p className="text-[10px] text-fg-muted mt-0.5">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-1" /> Pallets packed
          <span className="inline-block w-2 h-2 rounded-full bg-amber-400 ml-3 mr-1" /> Bins tipped
        </p>
      </div>
      <div className="flex gap-1">
        {([7, 14, 30] as const).map(r => (
          <button
            key={r}
            onClick={() => onRange(r)}
            aria-pressed={range === r}
            className={clsx(
              'tap-target-y text-[10px] font-semibold px-2 py-1 rounded-full transition-colors',
              range === r
                ? 'bg-green-600 text-white'
                : 'surface text-fg-muted hover:bg-raised'
            )}>
            {r}d
          </button>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
export default function PackoutTrendChart() {
  const [range, setRange] = useState<7 | 14 | 30>(30)
  // UF-09: folded on a phone until the manager opens it (then remembered);
  // open by default on a tablet or desktop. null = never chosen on this device.
  const isPhone = useDeviceClass() === 'phone'
  const [chosenOpen, setChosenOpen] = useTenantPersistedState<boolean | null>('home.trendOpen', null)
  const open = isPhone ? (chosenOpen ?? false) : true
  const granularity: Granularity = range === 7 ? 'hour' : 'day'
  const { data: points = [], isLoading, isError, isFetching, refetch } = useTrend(range, granularity)

  const totals = useMemo(() => {
    const pallets = points.reduce((s, p) => s + p.pallets, 0)
    const bins    = points.reduce((s, p) => s + p.bins, 0)
    // Bins per pallet — lower = better packout efficiency. Only meaningful
    // when both numbers exist for the period.
    const binsPerPallet = pallets > 0 && bins > 0 ? bins / pallets : null
    return { pallets, bins, binsPerPallet }
  }, [points])

  // Trend direction — compare last vs prior block of equivalent size.
  // Block = 3 days for daily granularity, last 24h vs prior 24h for hourly.
  // A *falling* bins-per-pallet ratio is better, so we flip the colour mapping.
  const trend = useMemo(() => {
    const blockSize = granularity === 'day' ? 3 : 24
    if (points.length < blockSize * 2) return null
    const ratio = (slice: Point[]) => {
      const p = slice.reduce((s, d) => s + d.pallets, 0)
      const b = slice.reduce((s, d) => s + d.bins, 0)
      return p > 0 && b > 0 ? b / p : null
    }
    const tail = ratio(points.slice(-blockSize))
    const prev = ratio(points.slice(-blockSize * 2, -blockSize))
    if (tail == null || prev == null) return null
    const pct = ((tail - prev) / prev) * 100
    return {
      dir: pct < -5 ? 'up' : pct > 5 ? 'down' : 'flat',
      pct: Math.round(pct),
      blockLabel: granularity === 'day' ? 'prior 3d' : 'prior 24h',
    }
  }, [points, granularity])

  // Hover-tooltip labels — full date+time for hourly, date for daily.
  const labels = points.map(p => {
    const d = new Date(p.ts)
    if (granularity === 'day') {
      return d.toLocaleDateString('en-ZA', {
        day: 'numeric', month: 'short', timeZone: 'Africa/Johannesburg',
      })
    }
    return d.toLocaleString('en-ZA', {
      weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: 'Africa/Johannesburg',
    })
  })

  // Compute SAST day boundaries + axis ticks based on granularity.
  // Daily view → first/middle/last; Hourly view → one tick at midday of each
  // SAST day (max 7), with faint vertical lines at SAST midnight.
  const { dayDividers, xTicks } = useMemo(() => {
    if (granularity === 'day' || points.length === 0) {
      return { dayDividers: [], xTicks: undefined }
    }

    const sastDayKey = (ts: number) =>
      new Date(ts + SAST_OFFSET_MS).toISOString().slice(0, 10)

    const dividers: number[] = []
    const dayStartIxs = new Map<string, number>()
    let prevKey = ''
    points.forEach((p, i) => {
      const key = sastDayKey(p.ts)
      if (key !== prevKey) {
        if (i > 0) dividers.push(i)
        dayStartIxs.set(key, i)
        prevKey = key
      }
    })

    // Tick at the middle bucket of each SAST day (≈ midday) — at most ~7 ticks.
    const dayKeys = Array.from(dayStartIxs.keys())
    const ticks: XTick[] = dayKeys.map((key, ki) => {
      const start = dayStartIxs.get(key)!
      const end   = ki + 1 < dayKeys.length ? dayStartIxs.get(dayKeys[ki + 1])! : points.length
      const mid   = Math.floor((start + end - 1) / 2)
      const d = new Date(points[mid].ts)
      return {
        index: mid,
        label: d.toLocaleDateString('en-ZA', {
          weekday: 'short', timeZone: 'Africa/Johannesburg',
        }),
      }
    })

    return { dayDividers: dividers, xTicks: ticks }
  }, [points, granularity])

  return (
    <div className="card p-4 mb-4">
      <TrendHeader
        range={range} onRange={setRange} granularity={granularity}
        isPhone={isPhone} open={open} onToggle={() => setChosenOpen(!open)}
      />

      {open && (
        <TrendBody
          range={range} totals={totals} trend={trend} points={points}
          labels={labels} xTicks={xTicks} dayDividers={dayDividers}
          isLoading={isLoading} isError={isError} isFetching={isFetching} refetch={refetch}
        />
      )}
    </div>
  )
}
